/**
 * Phase Keystone K5.2a/b — the synchronous clearance-ingestion resolver (the laddered-matching-funnel
 * seam). PURE (imports only the upcNormalize + matchScore leaves — the leaf precedent). Delivers the
 * no-duplicate-master guarantee structurally: a row RESOLVEs/INSERTs only on an EXACT identity match
 * (rungs 1–3); everything ambiguous HOLDs for human link-to-master — never auto-spawn, never fuzzy.
 *
 * INGESTION funnel — EXACT-ONLY (auto-resolve safe), evaluated 1 → 2 → 3 in order:
 *   Rung 1 — GTIN-exact (masters-only; UPC-12 + EAN-13 → one canonical-14 key) then,  [K5.2a; C3]
 *            ONLY on a GTIN miss/absent, ASIN-exact (secondary; GTIN stays canonical).
 *   Rung 2 — part#-exact, BRAND-qualified (normalized part# ∩ folded brand).     [K5.2b]
 *   Rung 3 — alias-exact (the learned, distributor-scoped supplier-SKU).         [K5.2b]
 *   else   — HOLD (cryptic) for human link-to-master.
 * Rung 2 ships NOW brand-qualified, reading `model` in place via the matchScore part# union — `model`
 * is the only fuel (catalog `mpn`/`supplier_part_no` are 0 AND unsourceable, so there is NO part#
 * backfill; the manufacturer-qualified tightening is the only K2-deferred arm). Every exact rung keeps
 * the rung-1 multiplicity guard: a part#/alias hitting ≥2 masters HOLDs (never auto-pick).
 *
 * FUZZY is QUEUE-SIDE ONLY: `suggestMasterCandidates` (below) is the resolution queue's on-demand
 * suggester. It is NEVER called at ingestion and NEVER auto-resolves — it only ranks link-to-master
 * candidates that a human then confirms.
 */
import { normalizeGtin } from './upcNormalize';
import {
  extractPartNoSet,
  normalizeBrand,
  scoreMatch,
  type MatchAttributes,
  type MatchVerdict,
} from './matchScore';

type ClearanceOutcome = 'resolve' | 'insert' | 'hold';

export interface ClearanceResolution {
  /** resolve = attach a child lot under masterId; insert = master+first lot; hold = human link-to-master. */
  outcome: ClearanceOutcome;
  /** set iff outcome === 'resolve'. */
  masterId?: string;
  /** which rung/branch decided, for diagnostics + the queue audit. */
  reason: string;
}

/**
 * The masters-only identity index, built by the ingestion hook from products WHERE parent_product_id
 * IS NULL (lots excluded — a lot copies its master's identity, so the match stays on the master). Keys:
 *   `gtin:<grade>:<CANONICAL14>` — rung 1 PRIMARY (C3): UPC-12 + EAN-13 fold to one GTIN-14 key via
 *                                  normalizeGtin (built + looked up through the SAME fn so cross-form
 *                                  pairs collide; C1 grade-segmented).
 *   `asin:<grade>:<VAL>`         — rung 1 SECONDARY (C3): consulted ONLY when the GTIN misses/absent
 *                                  (GTIN stays canonical); its own ≥2-master HOLD guard.
 *   `partno:<grade>:<brandFold>:<partNo>` — rung 2, where grade = condition_grade (the C1 identity
 *                                  axis), brandFold = normalizeBrand(brand) and partNo = each
 *                                  normalizePartNo token from the {supplier_part_no, mpn, model} union.
 * MULTIPLICITY matters — products has NO UNIQUE on upc/asin/part#, so a key may map to ≥2 masters,
 * which must HOLD (never auto-pick). Value = the master product id(s) carrying that key.
 */
export type MastersDedupMap = Map<string, string[]>;

/**
 * The distributor-scoped alias index for rung 3, built by the hook from product_aliases WHERE
 * distributor_id = the import's distributor. Key = the RAW seed expression (see `rawAliasKey`);
 * value = the single master id (UNIQUE(distributor_id, alias_sku) guarantees one per scoped key).
 */
export type AliasMap = Map<string, string>;

export interface ClearanceRowInput {
  upc: string | null | undefined;
  /** rung 1 SECONDARY key (C3) — consulted only when the GTIN misses/absent. */
  asin?: string | null | undefined;
  /** rung 2 (part# union) + rung 3 (raw alias key, primary). */
  supplier_part_no?: string | null | undefined;
  /** rung 2 (part# union) + rung 3 (raw alias key, fallback). */
  model?: string | null | undefined;
  /** rung 2 brand qualifier. */
  brand?: string | null | undefined;
  /**
   * C1 identity axis — the sellable-condition grade that segments the rung-1/rung-2 keys (so an
   * open-box row never resolves against a 'new' master). Optional + defaulted to 'new' inside
   * resolveClearanceRow: the ingestion hook omits it (C1 has no grade derivation — every row is
   * 'new'), and this is the seam future grade-derivation feeds. NOT applied to rung 3 (the alias is
   * grade-blind — rawAliasKey mirrors resolve_clearance_hold's raw seed verbatim).
   */
  condition_grade?: string | null | undefined;
}

/**
 * The rung-3 alias key — MIRRORS resolve_clearance_hold's seed VERBATIM:
 *   COALESCE(NULLIF(supplier_part_no,''), NULLIF(model,''))
 * i.e. the RAW supplier_part_no if non-empty, else the RAW model if non-empty, else null. It is
 * deliberately NOT normalizePartNo (GOTCHA 2) — the alias was seeded raw, so a normalized lookup
 * would miss it on re-import. No trim (NULLIF compares to '' only), matching the SQL exactly.
 */
function rawAliasKey(row: ClearanceRowInput): string | null {
  const spn = row.supplier_part_no;
  if (spn !== null && spn !== undefined && spn !== '') return spn;
  const model = row.model;
  if (model !== null && model !== undefined && model !== '') return model;
  return null;
}

/**
 * The secondary rung-1 ASIN lookup — consulted ONLY when the GTIN misses/absent (GTIN stays
 * canonical). Returns a RESOLVE (exactly 1 asin-master) or a HOLD (≥2 asin-masters — the ASIN
 * multiplicity guard mirroring the GTIN guard); returns null for a 0-master/absent ASIN so the
 * caller continues (insert-new when the GTIN was clean, else fall through to rung 2). NEVER
 * inserts-new itself — only the canonical GTIN anchors the irreversible auto-insert.
 */
function resolveByAsin(
  row: ClearanceRowInput,
  masters: MastersDedupMap,
  grade: string
): ClearanceResolution | null {
  const asin = typeof row.asin === 'string' && row.asin.trim() !== '' ? row.asin : '';
  if (asin === '') return null;
  const hits = masters.get(`asin:${grade}:${asin}`) ?? [];
  if (hits.length === 1) {
    return {
      outcome: 'resolve',
      masterId: hits[0],
      reason: 'rung1:asin-exact (1 master, gtin missed/absent)',
    };
  }
  if (hits.length >= 2) {
    return {
      outcome: 'hold',
      reason: `rung1:asin matches ${hits.length} masters -> ambiguous, hold`,
    };
  }
  return null;
}

/**
 * Decide the three-outcome disposition for one supplier_sheet (clearance) row — EXACT-ONLY funnel,
 * rungs 1 → 2 → 3 in order. The first exact hit wins; a ≥2-master hit on any rung HOLDs (the
 * multiplicity guard); all misses HOLD. NO fuzzy here.
 *
 *   @param row      the mapped supplier row (identity fields).
 *   @param masters  the masters-only identity index (upc:/asin:/partno: keys).
 *   @param aliasMap the distributor-scoped alias index (rung 3). Optional only so the pre-K5.2b
 *                   2-arg call still type-checks; the ingestion hook always passes it.
 */
export function resolveClearanceRow(
  row: ClearanceRowInput,
  masters: MastersDedupMap,
  aliasMap?: AliasMap
): ClearanceResolution {
  const upc = typeof row.upc === 'string' ? row.upc : '';
  // C1 identity axis — grade segments the rung-1/rung-2 keys. Defaulted to 'new' (the hook omits it in
  // C1; this is the seam future grade-derivation feeds). Rung 3 (alias) stays grade-blind.
  const grade = row.condition_grade ?? 'new';

  // ── Rung 1 — exact GTIN (PRIMARY), then exact ASIN (SECONDARY) ──────────────
  // GTIN is the canonical identity: UPC-12 + EAN-13 collapse to one canonical-14 key (normalizeGtin,
  // the SAME fn the masters map builds with, so cross-form pairs match). ASIN is consulted ONLY when
  // the GTIN misses (0 masters) or is absent — GTIN never yields to ASIN. INSERT-NEW is anchored on a
  // clean GTIN (the irreversible auto-path keeps the strongest gate): a clean GTIN with no master
  // (gtin AND asin) is confidently new; a row with no clean GTIN can only RESOLVE (exact ASIN) or fall
  // through — it never auto-inserts here.
  const gtin = normalizeGtin(upc);
  if (gtin !== null) {
    const hits = masters.get(`gtin:${grade}:${gtin}`) ?? [];
    if (hits.length === 1) {
      return { outcome: 'resolve', masterId: hits[0], reason: 'rung1:gtin-exact (1 master)' };
    }
    if (hits.length >= 2) {
      return {
        outcome: 'hold',
        reason: `rung1:gtin matches ${hits.length} masters -> ambiguous, hold`,
      };
    }
    // GTIN clean but absent from the catalog -> consult the secondary ASIN rung before insert-new.
    const viaAsin = resolveByAsin(row, masters, grade);
    if (viaAsin !== null) return viaAsin;
    return {
      outcome: 'insert',
      reason: 'rung1:gtin-clean, no master (gtin+asin) -> confidently new',
    };
  }

  // GTIN absent -> ASIN is the only rung-1 identity; it may RESOLVE/HOLD but never anchors insert-new
  // (no canonical GTIN), so a 0-master/absent ASIN falls through to rung 2.
  {
    const viaAsin = resolveByAsin(row, masters, grade);
    if (viaAsin !== null) return viaAsin;
  }

  // ── Rung 2 — part#-exact, BRAND-qualified ──────────────────────────────────
  // Eligible only when the row has BOTH a folded brand and ≥1 usable part# token. The brand fold
  // is matchScore.normalizeBrand itself (lower THEN strip — GOTCHA 1: a strip-then-lower drops every
  // all-uppercase brand). Part# = the normalizePartNo union over {supplier_part_no, model}.
  const brandFold = normalizeBrand(row.brand);
  const partNos = extractPartNoSet({
    supplierPartNo: row.supplier_part_no,
    model: row.model,
  });
  if (brandFold !== null && partNos.size > 0) {
    const matched = new Set<string>();
    for (const pn of partNos) {
      for (const id of masters.get(`partno:${grade}:${brandFold}:${pn}`) ?? []) matched.add(id);
    }
    if (matched.size === 1) {
      return {
        outcome: 'resolve',
        masterId: [...matched][0],
        reason: 'rung2:partno-exact, brand-qualified (1 master)',
      };
    }
    if (matched.size >= 2) {
      return {
        outcome: 'hold',
        reason: `rung2:partno+brand matches ${matched.size} masters -> ambiguous, hold`,
      };
    }
    // matched.size === 0 -> fall through to rung 3
  }

  // ── Rung 3 — alias-exact (learned, distributor-scoped supplier-SKU) ─────────
  if (aliasMap !== undefined) {
    const aliasKey = rawAliasKey(row);
    if (aliasKey !== null) {
      const masterId = aliasMap.get(aliasKey);
      if (masterId !== undefined) {
        return {
          outcome: 'resolve',
          masterId,
          reason: 'rung3:alias-exact (learned (distributor, supplier-sku))',
        };
      }
    }
  }

  // ── No exact hit -> HOLD (cryptic). The queue's suggestMasterCandidates surfaces fuzzy
  //    link-to-master candidates for a human; fuzzy NEVER auto-resolves at ingestion. ────────────
  return {
    outcome: 'hold',
    reason: 'rungs 1–3 (exact) missed -> hold for human link-to-master (queue fuzzy-suggests)',
  };
}

// ── Queue-side fuzzy suggester (NEVER called at ingestion) ──────────────────────────────────────

/** Default cap on suggested candidates surfaced per held row (tunable; the queue passes its own). */
const DEFAULT_SUGGEST_TOP_N = 5;

/** The held clearance row the queue scores against candidate masters (a intake_resolution_queue row). */
export interface SuggesterHeldRow {
  supplier_part_no?: string | null;
  model?: string | null;
  brand?: string | null;
  title?: string | null;
}

/** A candidate master the queue offers for link-to-master (a products master row). */
export interface SuggesterMaster {
  id: string;
  supplier_part_no?: string | null;
  mpn?: string | null;
  model?: string | null;
  brand?: string | null;
  title?: string | null;
}

/** One ranked link-to-master suggestion (advisory — the human still confirms the link). */
export interface MasterSuggestion {
  masterId: string;
  score: number;
  verdict: MatchVerdict;
}

/**
 * Rank candidate masters for a held clearance row by matchScore confidence (highest first), capped at
 * opts.topN (default DEFAULT_SUGGEST_TOP_N; pass 0/negative for no cap). PURE — the resolution queue
 * calls this ON-DEMAND against a pre-capped candidate pool (Phase 3 brand-filters + trigram-tops the
 * masters first, so this never scores against the whole catalog). It NEVER runs at ingestion and
 * NEVER auto-resolves; it only suggests — the human confirms via the existing resolve_clearance_hold.
 */
export function suggestMasterCandidates(
  heldRow: SuggesterHeldRow,
  candidateMasters: SuggesterMaster[],
  opts: { topN?: number } = {}
): MasterSuggestion[] {
  const a: MatchAttributes = {
    supplierPartNo: heldRow.supplier_part_no,
    model: heldRow.model,
    brand: heldRow.brand,
    title: heldRow.title,
  };
  const ranked = candidateMasters
    .map((m): MasterSuggestion => {
      const b: MatchAttributes = {
        supplierPartNo: m.supplier_part_no,
        mpn: m.mpn,
        model: m.model,
        brand: m.brand,
        title: m.title,
      };
      const r = scoreMatch(a, b);
      return { masterId: m.id, score: r.score, verdict: r.verdict };
    })
    .sort((x, y) => y.score - x.score);
  const n = opts.topN ?? DEFAULT_SUGGEST_TOP_N;
  return n > 0 ? ranked.slice(0, n) : ranked;
}
